import './InvoicePrint.css'

const ROWS_SINGLE_PAGE = 14    // Header + Table + Footer on one page
const ROWS_FIRST_PAGE_MAX = 22 // Header + Table (no footer) max
const ROWS_MIDDLE_PAGE = 24    // Table only (no header, no footer)
const ROWS_LAST_PAGE = 17      // Table + Footer (no header) max
const MIN_LAST_PAGE_ITEMS = 2  // Last page must have at least this many items

const PAYMENT_LABELS: Record<string, string> = {
  CASH: 'Cash / Tunai',
  BANK_TRANSFER: 'Bank Transfer',
  CREDIT: 'Credit / Tempo',
}

const fmt = (n: any): string => {
  if (!n && n !== 0) return '0'
  return Number(n).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const fmtQty = (n: any): string => {
  if (!n && n !== 0) return '0'
  const num = Number(n)
  if (num % 1 === 0) return num.toFixed(0)
  return num.toFixed(2)
}

const fmtDate = (d: string): string => {
  if (!d) return ''
  const dt = new Date(d + 'T00:00:00')
  const dd = String(dt.getDate()).padStart(2, '0')
  const mm = String(dt.getMonth() + 1).padStart(2, '0')
  const yyyy = dt.getFullYear()
  return `${dd}-${mm}-${yyyy}`
}

const COL_WIDTH_CHARS = 28 // max chars that fit in one line of nama_barang column

function itemLineCount(item: any): number {
  const text = (item.nama_barang || '').trim()
  if (text.length <= COL_WIDTH_CHARS) return 1
  const words = text.split(/\s+/)
  let lines = 1
  let currentLen = 0
  for (const word of words) {
    const wordLines = Math.ceil(word.length / COL_WIDTH_CHARS)
    const wordLen = wordLines > 1 ? COL_WIDTH_CHARS : word.length
    if (currentLen === 0) {
      lines += wordLines - 1
      currentLen = word.length - (wordLines - 1) * COL_WIDTH_CHARS
    } else if (currentLen + 1 + wordLen > COL_WIDTH_CHARS) {
      lines += wordLines
      currentLen = word.length - (wordLines - 1) * COL_WIDTH_CHARS
    } else {
      currentLen += 1 + word.length
    }
  }
  return lines
}

function paginateItems(items: any[]) {
  const pages: { items: any[], type: 'single' | 'first' | 'middle' | 'last' }[] = []

  const totalLines = items.reduce((s, i) => s + itemLineCount(i), 0)

  if (totalLines <= ROWS_SINGLE_PAGE) {
    pages.push({ items, type: 'single' })
    return pages
  }

  let remaining = [...items]

  // First page: fill up to ROWS_FIRST_PAGE_MAX lines
  let firstItems: any[] = []
  let firstLines = 0
  while (remaining.length > 0) {
    const lines = itemLineCount(remaining[0])
    if (firstLines + lines > ROWS_FIRST_PAGE_MAX) break
    firstLines += lines
    firstItems.push(remaining.shift()!)
  }
  // Ensure last page has at least MIN_LAST_PAGE_ITEMS
  while (remaining.length < MIN_LAST_PAGE_ITEMS && firstItems.length > MIN_LAST_PAGE_ITEMS) {
    remaining.unshift(firstItems.pop()!)
  }
  pages.push({ items: firstItems, type: 'first' })

  // Middle pages
  while (true) {
    const remainingLines = remaining.reduce((s, i) => s + itemLineCount(i), 0)
    if (remainingLines <= ROWS_LAST_PAGE) break

    let middleItems: any[] = []
    let middleLines = 0
    while (remaining.length > 0) {
      const lines = itemLineCount(remaining[0])
      if (middleLines + lines > ROWS_MIDDLE_PAGE) break
      middleLines += lines
      middleItems.push(remaining.shift()!)
    }
    // If remaining would be empty, keep at least MIN_LAST_PAGE_ITEMS for last page
    while (remaining.length < MIN_LAST_PAGE_ITEMS && middleItems.length > MIN_LAST_PAGE_ITEMS) {
      remaining.unshift(middleItems.pop()!)
    }
    pages.push({ items: middleItems, type: 'middle' })
  }

  pages.push({ items: remaining, type: 'last' })
  return pages
}

/* ---- Table Header ---- */
function TableHeader() {
  return (
    <thead>
      <tr>
        <th className="col-no">NO</th>
        <th className="col-nama">Nama Barang</th>
        <th className="col-qty">QTY</th>
        <th className="col-satuan">Satuan</th>
        <th className="col-harga">Harga</th>
        <th className="col-discount">Discount</th>
        <th className="col-total">Total Harga</th>
      </tr>
    </thead>
  )
}

/* ---- Item Rows ---- */
function ItemRows({ items }: { items: any[] }) {
  return (
    <tbody>
      {items.map((item: any) => (
        <tr key={item.seq}>
          <td className="col-no">{item.seq}</td>
          <td className="col-nama">{item.nama_barang}</td>
          <td className="col-qty">{fmtQty(item.qty)}</td>
          <td className="col-satuan">{item.satuan}</td>
          <td className="col-harga">{fmt(item.harga)}</td>
          <td className="col-discount">{Number(item.discount) > 0 ? fmt(item.discount) : '-'}</td>
          <td className="col-total">{fmt(item.total_harga)}</td>
        </tr>
      ))}
    </tbody>
  )
}

/* ---- Intermediate Total Row (after last item) ---- */
function IntermediateRow({ invoice, items }: { invoice: any, items: any[] }) {
  const qtySum = items.reduce((s: number, i: any) => s + Number(i.qty), 0)
  const discSum = items.reduce((s: number, i: any) => s + Number(i.discount), 0)
  const subTotal = items.reduce((s: number, i: any) => s + Number(i.total_harga), 0)

  return (
    <tbody>
      <tr className="intermediate-row">
        <td className="col-no"></td>
        <td className="col-nama" style={{ fontSize: '8.5pt' }}>
          Pembayaran: {PAYMENT_LABELS[invoice.payment_type] || invoice.payment_type}
        </td>
        <td className="col-qty">{fmt(qtySum)}</td>
        <td className="col-satuan"></td>
        <td className="col-harga"></td>
        <td className="col-discount">{discSum > 0 ? fmt(discSum) : '-'}</td>
        <td className="col-total">{fmt(subTotal)}</td>
      </tr>
    </tbody>
  )
}

/* ---- First Page Header ---- */
function InvoicePageHeader({ invoice }: { invoice: any }) {
  return (
    <div className="invoice-header">
      <div className="invoice-title-row">
        <div className="invoice-title-center">SALES INVOICE</div>
        <div className="invoice-title-right">FAKTUR PENJUALAN</div>
      </div>

      <div className="invoice-meta">
        <div className="meta-column-left">
          <div className="meta-row">
            <span className="meta-label">No / Nomor</span>
            <span className="meta-colon">:</span>
            <span className="meta-value">{invoice.invoice_number}</span>
          </div>
          <div className="meta-row">
            <span className="meta-label">Nama / Customer</span>
            <span className="meta-colon">:</span>
            <span className="meta-value">{invoice.customer_name}</span>
          </div>
        </div>
        <div className="meta-column-right">
          <div className="meta-row">
            <span className="meta-label">Tgl / Tanggal</span>
            <span className="meta-colon">:</span>
            <span className="meta-value">{fmtDate(invoice.invoice_date)}</span>
          </div>
          <div className="meta-row">
            <span className="meta-label">Alamat / Address</span>
            <span className="meta-colon">:</span>
            <span className="meta-value">{invoice.customer_address || '-'}</span>
          </div>
        </div>
        <div className="clear-fix" />
      </div>
    </div>
  )
}

/* ---- Continuation Header (page 2+) ---- */
function ContinuationHeader({ invoice }: { invoice: any }) {
  return (
    <div className="continuation-header">
      <span>Faktur Penjualan (lanjutan) — {invoice.customer_name}</span>
      <span>No: {invoice.invoice_number} | Tgl: {fmtDate(invoice.invoice_date)}</span>
    </div>
  )
}

/* ---- Footer: 3-column block ---- */
function Footer({ invoice }: { invoice: any }) {
  const subtotal = invoice.items.reduce((s: number, i: any) => s + Number(i.total_harga), 0)
  const discount = Number(invoice.discount_total)
  const grand = Number(invoice.grand_total)
  const qtySum = invoice.items.reduce((s: number, i: any) => s + Number(i.qty), 0)
  const discSum = invoice.items.reduce((s: number, i: any) => s + Number(i.discount), 0)

  return (
    <div className="footer-block">
      <div className="footer-payment-row">
        <span>Pembayaran: {PAYMENT_LABELS[invoice.payment_type] || invoice.payment_type}</span>
        <span>Qty: {fmt(qtySum)}</span>
        <span>Discount: {discSum > 0 ? fmt(discSum) : '-'}</span>
        <span>Sub Total: {fmt(subtotal)}</span>
      </div>

      <div className="footer-columns">
        <div className="f-col-1 footer-signature">
          <div>Tanda Terima</div>
          <div className="sig-placeholder">(------------------)</div>
        </div>

        <div className="f-col-2 footer-notice">
          <div className="notice-title">PERHATIAN |||</div>
          <div>
            Pembayaran Via Mbanking kami anggap SAH apabila mencantumkan nama atau no invoice
            pada berita transfer, sesuai yang tertera pada Nota.
          </div>
        </div>

        <div className="f-col-3 footer-totals">
          <table>
            <tbody>
              <tr>
                <td className="t-label">Total</td>
                <td className="t-value">{fmt(subtotal)}</td>
              </tr>
              <tr>
                <td className="t-label">Discount</td>
                <td className="t-value">{discount > 0 ? `(${fmt(discount)})` : '-'}</td>
              </tr>
              <tr className="grand-row">
                <td className="t-label">Grand Total</td>
                <td className="t-value">{fmt(grand)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="clear-fix" />
      </div>
    </div>
  )
}

/* ---- Main Component ---- */
export default function InvoicePrint({ invoice, renderMode = 'print' }: { invoice: any, renderMode?: 'print' | 'preview' }) {
  const pages = paginateItems(invoice.items)
  const totalPages = pages.length

  if (renderMode === 'preview') {
    return (
      <>
        {pages.map((page, idx) => {
          const pageNum = idx + 1
          const showHeader = page.type === 'single' || page.type === 'first'
          const showFooter = page.type === 'single' || page.type === 'last'

          return (
            <div className="preview-page-wrapper" key={idx}>
              <div className="preview-page">
                <div className="invoice-container">
                  <div className="invoice-body">
                    <div className="page-number">Page {pageNum}/{totalPages}</div>
                    {showHeader
                      ? <InvoicePageHeader invoice={invoice} />
                      : <ContinuationHeader invoice={invoice} />
                    }
                    <table className="item-table">
                      <TableHeader />
                      <ItemRows items={page.items} />
                    </table>
                  </div>
                  {showFooter && (
                    <div className="invoice-footer">
                      <Footer invoice={invoice} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </>
    )
  }

  return (
    <div className="print-preview-wrapper">
      {pages.map((page, idx) => {
        const pageNum = idx + 1
        const showHeader = page.type === 'single' || page.type === 'first'
        const showFooter = page.type === 'single' || page.type === 'last'

        return (
          <div className="print-preview-sheet" key={idx}>
            <div className="invoice-container">

              <div className="invoice-body">
                <div className="page-number">Page {pageNum}/{totalPages}</div>

                {showHeader
                  ? <InvoicePageHeader invoice={invoice} />
                  : <ContinuationHeader invoice={invoice} />
                }

                <table className="item-table">
                  <TableHeader />
                  <ItemRows items={page.items} />
                </table>
              </div>

              {showFooter && (
                <div className="invoice-footer">
                  <Footer invoice={invoice} />
                </div>
              )}

            </div>
          </div>
        )
      })}
    </div>
  )
}
